"use client";

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { showToast } from "@/components/ui/sonner";
import {
  createScorecardLibraryDefinition,
  getListScorecardLibraryQueryKey,
  listScorecardLibrary,
  reorderScorecardLibrary,
  updateScorecardLibraryDefinition,
} from "@/lib/api/generated";
import type {
  ScorecardDefinitionCreate,
  ScorecardDefinitionOut,
  ScorecardDefinitionPatch,
  ScorecardOrderUpdate,
} from "@/lib/api/model";

import { apiErrorMessage, jobScorecardDefinitionsQueryFilter } from "./use-definitions";

export function useScorecardLibrary(includeArchived = false) {
  return useQuery({
    queryKey: getListScorecardLibraryQueryKey({ include_archived: includeArchived }),
    queryFn: () => listScorecardLibrary({ include_archived: includeArchived }),
    placeholderData: keepPreviousData,
  });
}

function useInvalidateLibrary({ includeJobs = false } = {}) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: getListScorecardLibraryQueryKey() });
    if (includeJobs) {
      void qc.invalidateQueries(jobScorecardDefinitionsQueryFilter());
    }
  };
}

export function useCreateLibraryDefinition() {
  const invalidate = useInvalidateLibrary({ includeJobs: true });
  return useMutation({
    mutationFn: (body: ScorecardDefinitionCreate) => createScorecardLibraryDefinition(body),
    onSuccess: () => {
      invalidate();
      showToast.success("Scorecard type created");
    },
    onError: (err) =>
      showToast.error("Could not create scorecard type", { description: apiErrorMessage(err) }),
  });
}

export function useUpdateLibraryDefinition() {
  const invalidate = useInvalidateLibrary({ includeJobs: true });
  return useMutation({
    mutationFn: ({
      definitionId,
      body,
    }: {
      definitionId: string;
      body: ScorecardDefinitionPatch;
      successMessage?: string;
    }) => updateScorecardLibraryDefinition(definitionId, body),
    onSuccess: (_data, vars) => {
      invalidate();
      showToast.success(vars.successMessage ?? "Library updated");
    },
    onError: (err) =>
      showToast.error("Could not update the library", { description: apiErrorMessage(err) }),
  });
}

export function useReorderLibrary() {
  const qc = useQueryClient();
  const invalidate = useInvalidateLibrary();
  return useMutation({
    mutationFn: (body: ScorecardOrderUpdate) => reorderScorecardLibrary(body),
    onMutate: async (body) => {
      const key = getListScorecardLibraryQueryKey();
      await qc.cancelQueries({ queryKey: key });
      const snapshot = qc.getQueriesData<ScorecardDefinitionOut[]>({ queryKey: key });
      qc.setQueriesData<ScorecardDefinitionOut[]>({ queryKey: key }, (old) => {
        if (!old) return old;
        const byId = new Map(old.map((d) => [d.id, d]));
        const next = body.ids
          .map((id) => byId.get(id))
          .filter((d): d is ScorecardDefinitionOut => Boolean(d));
        const rest = old.filter((d) => !body.ids.includes(d.id));
        return [...next, ...rest];
      });
      return { snapshot };
    },
    onError: (err, _vars, context) => {
      if (context?.snapshot) {
        for (const [queryKey, data] of context.snapshot) {
          qc.setQueryData(queryKey, data);
        }
      }
      showToast.error("Could not reorder", { description: apiErrorMessage(err) });
    },
    onSettled: invalidate,
  });
}
